"use client";

import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { format, isSameDay, max, min, startOfDay } from "date-fns";
import { useEffect, useState } from "react";
import { useIsMobile } from "../lib/hooks/use-mobile";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "./ui/carousel";

export type CarouselCalendarProps = {
  dates: Date[];
  currentDate: Date;
  setCurrentDate: (date: string) => void;
  className?: string;
  locale?: string;
  previousButtonLabel?: string;
  nextButtonLabel?: string;
};

const getDaysArray = (start: Date, end: Date) => {
  const days: Date[] = [];

  for (
    const date = new Date(startOfDay(start));
    date <= new Date(startOfDay(end));
    date.setDate(date.getDate() + 1)
  ) {
    days.push(new Date(date));
  }

  return days;
};

const useCarouselDrag = (api: CarouselApi | undefined) => {
  const isMobile = useIsMobile();
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!api) {
      return;
    }

    if (!isMobile) {
      return;
    }

    const handlePointerDown = () => setIsDragging(true);
    const handlePointerUp = () => setIsDragging(false);

    api.on("pointerDown", handlePointerDown);
    api.on("pointerUp", handlePointerUp);

    return () => {
      api.off("pointerDown", handlePointerDown);
      api.off("pointerUp", handlePointerUp);
    };
  }, [api, isMobile]);

  return isDragging;
};

const isValidDate = (date: Date) => !Number.isNaN(date.getTime());

export const CarouselCalendar = ({
  dates,
  currentDate,
  setCurrentDate,
  className,
  locale = "pl-PL",
  previousButtonLabel = "Previous dates",
  nextButtonLabel = "Next dates",
}: CarouselCalendarProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  const isDragging = useCarouselDrag(api);
  const allDates = dates.length > 0 ? getDaysArray(min(dates), max(dates)) : [];
  const hasValidCurrentDate = isValidDate(currentDate);
  const activeIndex = hasValidCurrentDate
    ? allDates.findIndex((date) => isSameDay(date, currentDate))
    : -1;

  useEffect(() => {
    if (!api) {
      return;
    }

    const handleSelect = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };

    handleSelect();
    api.on("select", handleSelect);
    api.on("reInit", handleSelect);

    return () => {
      api.off("select", handleSelect);
      api.off("reInit", handleSelect);
    };
  }, [api]);

  // Bring the active day into view automatically when it is off-screen, so the
  // user does not have to scroll the carousel manually. Skip when it is already
  // visible to avoid re-snapping the view on every selection. slidesToScroll
  // groups slides into snaps, so map the active slide index to its snap.
  useEffect(() => {
    if (!api || activeIndex < 0) {
      return;
    }

    const scrollToActive = () => {
      if (api.slidesInView().includes(activeIndex)) {
        return;
      }
      const snapIndex = api
        .internalEngine()
        .slideRegistry.findIndex((group) => group.includes(activeIndex));
      api.scrollTo(snapIndex === -1 ? activeIndex : snapIndex, true);
    };

    scrollToActive();
    api.on("reInit", scrollToActive);

    return () => {
      api.off("reInit", scrollToActive);
    };
  }, [api, activeIndex]);

  if (dates.length === 0) {
    return null;
  }

  return (
    <Carousel
      className={cn("relative px-4 pb-4", className)}
      opts={{ dragFree: true, slidesToScroll: 4, align: "start" }}
      setApi={setApi}
    >
      <CarouselContent className="mr-4 space-x-2">
        {allDates.map((date) => {
          const isActive = hasValidCurrentDate
            ? isSameDay(date, currentDate)
            : false;
          const isDisabled = !dates.some((selectedDate) =>
            isSameDay(selectedDate, date),
          );
          let hoverClassName: string | undefined;

          if (!isDisabled) {
            if (!isActive) {
              hoverClassName = "hover:bg-background-fill-primary-hover";
            }
          }
          const dayOfWeek = date.toLocaleString(locale, {
            weekday: "short",
          });
          const day = date.getDate();
          const month = date.toLocaleString(locale, {
            month: "short",
          });

          return (
            <CarouselItem
              className="h-[72px] w-[80px] basis-auto"
              key={date.toISOString()}
            >
              <button
                aria-current={isActive ? "date" : undefined}
                aria-label={`${dayOfWeek} ${day} ${month}`}
                className={cn(
                  "flex h-[72px] w-[80px] cursor-pointer flex-col items-center justify-center gap-y-1 rounded-lg border bg-white py-2",
                  hoverClassName,
                  {
                    "border-transparent bg-brand-green2 text-brand-white":
                      isActive,
                    "cursor-default bg-brand-grey2 opacity-30": isDisabled,
                  },
                )}
                disabled={isDisabled}
                onClick={() => setCurrentDate(format(date, "yyyy-MM-dd"))}
                type="button"
              >
                <span className="font-medium text-xs uppercase leading-5">
                  {dayOfWeek}
                </span>
                <span className="font-medium text-3xl leading-5">{day}</span>
                <span className="font-medium text-xs uppercase leading-5">
                  {month}
                </span>
              </button>
            </CarouselItem>
          );
        })}
      </CarouselContent>
      {!isDragging && (
        <>
          <Button
            aria-label={previousButtonLabel}
            className={cn(
              "absolute top-1/2 left-0 z-10 hidden h-12 w-12 -translate-y-1/2 rounded-xl border bg-brand-white p-1 hover:bg-brand-green-superlight",
              current !== 0 && "lg:flex",
            )}
            onClick={() => api?.scrollTo(current - 1)}
            size="lg"
            type="button"
            variant="ghost"
          >
            <IconChevronLeft className="h-6 w-6" />
          </Button>
          <Button
            aria-label={nextButtonLabel}
            className={cn(
              "absolute top-1/2 right-0 z-10 hidden h-12 w-12 -translate-y-1/2 rounded-xl border bg-brand-white p-1 hover:bg-brand-green-superlight",
              current + 1 !== count && "lg:flex",
            )}
            onClick={() => api?.scrollTo(current + 1)}
            size="lg"
            type="button"
            variant="ghost"
          >
            <IconChevronRight className="h-6 w-6" />
          </Button>
        </>
      )}
    </Carousel>
  );
};
